// Web Quick Log page — recent wheel + catalog pick + camera UPC scan.
// Camera scan uses the browser's BarcodeDetector API (Chromium-based:
// Chrome, Edge, Samsung Internet, Brave). On unsupported browsers we
// surface a clean message and the manual flows still work. The scan
// resolves a UPC through the same VPS endpoint the APK uses
// (https://mystack.ai-ministries.com/api/upc/lookup).

import { COMPOUNDS, COMPOUND_BY_ID, type Compound } from '../lib/compounds';
import { loadAll as loadAllConsumption, recentUnique, addConsumption, type ConsumptionEntry } from '../lib/consumption_store';
import { navigate } from '../lib/router';

let _recent: ConsumptionEntry[] = [];

export async function renderQuickLog() {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <div class="detail-header">
      <h1>Quick log</h1>
      <p class="sci">Logging something you just consumed (Mt Dew, snack, supplement) — separate from your planned stacks.</p>
    </div>
    <section class="detail-section" id="recent-section">
      <h3>Recent</h3>
      <div id="recent-list" style="display:flex;flex-direction:column;gap:8px;">Loading…</div>
    </section>

    <section class="detail-section">
      <h3>Add new</h3>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button id="scan-upc" class="btn btn-primary">📷 Scan UPC</button>
        <button id="pick-from-catalog" class="btn">Search catalog</button>
      </div>
    </section>

    <section class="detail-section">
      <h3>Today's log</h3>
      <div id="today-list" style="display:flex;flex-direction:column;gap:6px;">Loading…</div>
    </section>
  `;

  _recent = await recentUnique();
  paintRecent();
  paintToday();

  document.getElementById('pick-from-catalog')?.addEventListener('click', () => openPicker());
  document.getElementById('scan-upc')?.addEventListener('click', () => openUpcScanner());
}

const UPC_LOOKUP_URL = 'https://mystack.ai-ministries.com/api/upc/lookup';

async function openUpcScanner() {
  // Feature-detect the native BarcodeDetector. Available on Chromium-based
  // browsers (Chrome, Edge, Samsung Internet, Brave) on Android/desktop;
  // not yet on Firefox or iOS Safari.
  const BD = (window as any).BarcodeDetector;
  if (!BD) {
    toast('Barcode scanning not supported on this browser — try Chrome/Edge or use the APK.');
    return;
  }

  const back = document.createElement('div');
  back.className = 'ats-back';
  back.innerHTML = `
    <div class="ats-box" style="max-width:480px;">
      <h3>Scan a UPC</h3>
      <video id="upc-video" autoplay muted playsinline
        style="width:100%;max-height:55vh;background:#000;border-radius:8px;"></video>
      <p id="upc-status" style="color:var(--dim);font-size:12px;margin:8px 0 0;">Point your camera at the barcode.</p>
      <div class="ats-actions">
        <button id="upc-cancel" class="btn">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(back);

  const video = back.querySelector('#upc-video') as HTMLVideoElement;
  const status = back.querySelector('#upc-status') as HTMLElement;
  let stream: MediaStream | null = null;
  let stopped = false;
  const cleanup = () => { stopped = true; stream?.getTracks().forEach((t) => t.stop()); back.remove(); };
  back.querySelector('#upc-cancel')?.addEventListener('click', cleanup);
  back.addEventListener('click', (e) => { if (e.target === back) cleanup(); });

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
      audio: false,
    });
    video.srcObject = stream;
  } catch (e: any) {
    status.textContent = `Camera error: ${e?.message ?? 'permission denied'}`;
    return;
  }

  const detector = new BD({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'] });

  const tick = async () => {
    if (stopped) return;
    try {
      const codes = await detector.detect(video);
      if (codes && codes.length > 0) {
        const upc = codes[0].rawValue as string;
        status.textContent = `Found: ${upc} — looking up…`;
        cleanup();
        await handleScannedUpc(upc);
        return;
      }
    } catch {/* per-frame errors are normal until camera warms up */}
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

async function handleScannedUpc(upc: string) {
  try {
    const r = await fetch(`${UPC_LOOKUP_URL}?upc=${encodeURIComponent(upc)}`);
    if (!r.ok) { toast(`No match for ${upc}`); return; }
    const data = await r.json();
    // Endpoint returns either { compound_id, display_name } or { unknown: true }
    if (data?.compound_id && COMPOUND_BY_ID[data.compound_id]) {
      openQtyPicker(COMPOUND_BY_ID[data.compound_id]);
    } else if (data?.display_name) {
      toast(`Found "${data.display_name}" — not in your catalog yet.`);
    } else {
      toast(`No match for ${upc}`);
    }
  } catch (e: any) {
    toast(`Lookup failed: ${e?.message ?? e}`);
  }
}

function paintRecent() {
  const wrap = document.getElementById('recent-list');
  if (!wrap) return;
  if (_recent.length === 0) {
    wrap.innerHTML = `<div style="color:var(--dim);padding:12px;background:var(--bg-2);border:1px solid var(--line);border-radius:8px;font-size:13px;">No recent quick logs yet. Use "Search catalog" below to add your first one.</div>`;
    return;
  }
  wrap.innerHTML = _recent.map((e) => {
    const c = COMPOUND_BY_ID[e.compound_id];
    const name = c?.display_name ?? e.compound_id;
    const qtyStr = e.qty != null ? `${e.qty} × ` : '';
    const unitStr = e.qty_unit ? ` (${esc(e.qty_unit)})` : '';
    return `<button class="recent-relog" data-id="${esc(e.id)}"
              style="text-align:left;padding:12px 14px;background:var(--bg-2);border:1px solid var(--acc);border-radius:8px;color:var(--ink);cursor:pointer;">
              <div style="font-weight:700;">${qtyStr}${esc(name)}${unitStr}</div>
              <div style="color:var(--acc);font-size:11px;margin-top:2px;">Tap to re-log right now</div>
            </button>`;
  }).join('');
  wrap.querySelectorAll('.recent-relog').forEach((b) => {
    b.addEventListener('click', async () => {
      const id = (b as HTMLElement).dataset.id;
      if (!id) return;
      const e = _recent.find((x) => x.id === id);
      if (!e) return;
      await addConsumption({
        compound_id: e.compound_id,
        qty: e.qty,
        qty_unit: e.qty_unit,
        source: e.source,
      });
      const c = COMPOUND_BY_ID[e.compound_id];
      toast(`Logged ${e.qty ?? 1} × ${c?.display_name ?? e.compound_id}`);
      paintToday();
    });
  });
}

async function paintToday() {
  const wrap = document.getElementById('today-list');
  if (!wrap) return;
  const all = await loadAllConsumption();
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const today = all.filter((e) => new Date(e.consumed_at).getTime() >= start);
  if (today.length === 0) {
    wrap.innerHTML = `<div style="color:var(--dim);font-size:13px;">Nothing logged today yet.</div>`;
    return;
  }
  wrap.innerHTML = today.map((e) => {
    const c = COMPOUND_BY_ID[e.compound_id];
    const name = c?.display_name ?? e.compound_id;
    const qtyStr = e.qty != null ? `${e.qty} × ` : '';
    const unitStr = e.qty_unit ? ` (${esc(e.qty_unit)})` : '';
    const t = new Date(e.consumed_at);
    const tStr = `${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')}`;
    return `<div style="padding:10px;background:var(--bg-3);border-radius:6px;display:flex;align-items:center;gap:10px;">
              <span style="color:var(--dim);font-size:11px;width:48px;">${tStr}</span>
              <span style="flex:1;">${qtyStr}${esc(name)}${unitStr}</span>
            </div>`;
  }).join('');
}

function openPicker() {
  const back = document.createElement('div');
  back.className = 'ats-back';
  back.innerHTML = `
    <div class="ats-box" style="max-width:560px;">
      <h3>Pick what you consumed</h3>
      <input id="ql-search" type="search" placeholder="Search compounds…" autofocus
        style="width:100%;padding:10px;background:var(--bg-3);border:1px solid var(--line);border-radius:6px;color:var(--ink);box-sizing:border-box;" />
      <div id="ql-results" style="max-height:300px;overflow-y:auto;margin-top:10px;display:flex;flex-direction:column;gap:4px;"></div>
      <div class="ats-actions">
        <button id="ql-cancel" class="btn">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(back);
  const input = back.querySelector('#ql-search') as HTMLInputElement;
  const results = back.querySelector('#ql-results') as HTMLElement;
  const repaint = () => {
    const q = input.value.trim().toLowerCase();
    const list = q
      ? COMPOUNDS.filter((c) =>
          c.display_name.toLowerCase().includes(q) ||
          c.scientific_name.toLowerCase().includes(q) ||
          c.aliases.some((a) => a.toLowerCase().includes(q))).slice(0, 30)
      : COMPOUNDS.slice(0, 30);
    results.innerHTML = list.map((c) => `<button class="ql-pick" data-id="${esc(c.id)}"
          style="text-align:left;padding:8px 10px;background:var(--bg-3);border:1px solid var(--line);border-radius:6px;color:var(--ink);cursor:pointer;">
          <div style="font-weight:600;">${esc(c.display_name)}</div>
          ${c.scientific_name ? `<div style="color:var(--dim);font-size:11px;">${esc(c.scientific_name)}</div>` : ''}
        </button>`).join('');
    results.querySelectorAll('.ql-pick').forEach((b) => {
      b.addEventListener('click', () => {
        const id = (b as HTMLElement).dataset.id;
        const c = id ? COMPOUND_BY_ID[id] : null;
        if (!c) return;
        back.remove();
        openQtyPicker(c);
      });
    });
  };
  input.addEventListener('input', repaint);
  back.querySelector('#ql-cancel')?.addEventListener('click', () => back.remove());
  back.addEventListener('click', (e) => { if (e.target === back) back.remove(); });
  repaint();
}

function openQtyPicker(c: Compound) {
  const sources = c.sources || ['serving'];
  const back = document.createElement('div');
  back.className = 'ats-back';
  back.innerHTML = `
    <div class="ats-box">
      <h3>Log ${esc(c.display_name)}</h3>
      <label>Quantity</label>
      <input id="ql-qty" type="number" step="0.01" value="1" />
      <label>Source / unit</label>
      <select id="ql-source">
        ${sources.map((s) => `<option value="${esc(s)}">${esc(s)}</option>`).join('')}
      </select>
      <label>When</label>
      <input id="ql-when" type="time" value="${new Date().toTimeString().slice(0, 5)}" />
      <p style="color:var(--dim);font-size:11px;">Today's date is assumed. Edit time to backdate within today.</p>
      <div class="ats-actions">
        <button id="ql-cancel2" class="btn">Cancel</button>
        <button id="ql-save" class="btn btn-primary">Log it</button>
      </div>
    </div>
  `;
  document.body.appendChild(back);
  back.querySelector('#ql-cancel2')?.addEventListener('click', () => back.remove());
  back.addEventListener('click', (e) => { if (e.target === back) back.remove(); });
  back.querySelector('#ql-save')?.addEventListener('click', async () => {
    const qty = parseFloat((back.querySelector('#ql-qty') as HTMLInputElement).value);
    const src = (back.querySelector('#ql-source') as HTMLSelectElement).value;
    const t = (back.querySelector('#ql-when') as HTMLInputElement).value;
    const now = new Date();
    const [h, m] = (t || '00:00').split(':');
    const when = new Date(now.getFullYear(), now.getMonth(), now.getDate(), +h, +m);
    await addConsumption({ compound_id: c.id, qty, qty_unit: src, source: src, consumed_at: when });
    back.remove();
    toast(`Logged ${qty} × ${c.display_name}`);
    _recent = await recentUnique();
    paintRecent();
    paintToday();
  });
}

function toast(msg: string) {
  const t = document.createElement('div');
  t.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--acc);color:#000;padding:12px 18px;border-radius:8px;font-weight:700;z-index:10000;';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 2200);
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Re-export navigate so other pages can link here without importing router directly.
export { navigate };
